import { ClarionButton } from '@clarion/ClarionButton';
import { Icon } from '@clarion/ClarionIcon';
import { captureException } from '@sentry/react';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';
import NonIdealPage from '../util/NonIdealPage';

export function EmbedErrorBoundary() {
  const error = useRouteError() as Error;

  useEffect(() => {
    console.error(error);
    captureException(error);
  }, [error]);

  const is401Error = (err: unknown) => {
    if (typeof err === 'number') return err === 401;
    const e = err as any;
    const message = String(e?.message ?? '').toLowerCase();
    return (
      e?.status === 401 ||
      e?.cause?.status === 401 ||
      e?.response?.status === 401 ||
      message.includes('http 401') ||
      message.includes('unauthorized')
    );
  };

  const is403Error = (err: unknown) => {
    if (typeof err === 'number') return err === 403;
    const e = err as any;
    const message = String(e?.message ?? '').toLowerCase();
    return (
      e?.status === 403 ||
      e?.cause?.status === 403 ||
      e?.response?.status === 403 ||
      message.includes('http 403') ||
      message.includes('forbidden') ||
      message.includes('invalid api key')
    );
  };

  const is404Error = (err: unknown) => {
    if (typeof err === 'number') return err === 404 || err === 412;
    const e = err as any;
    const message = String(e?.message ?? '').toLowerCase();
    return (
      e?.status === 404 ||
      e?.status === 412 ||
      e?.cause?.status === 404 ||
      e?.cause?.status === 412 ||
      e?.response?.status === 404 ||
      e?.response?.status === 412 ||
      message.includes('http 404') ||
      message.includes('http 412') ||
      message.includes('not found') ||
      message.includes('precondition failed') ||
      message.includes('missing file id') ||
      message.includes('missing workspace id')
    );
  };

  const isNotFoundError = is404Error(error);
  const isAuthError = is401Error(error) || is403Error(error);

  // Check for 404/412 errors first, as 412 (Precondition Failed) indicates invalid input
  // which should be treated as "not found" rather than "unauthorized"
  if (isNotFoundError) {
    return (
      <NonIdealPage
        icon="search"
        title="Content Not Found"
        description="The content you're looking for doesn't exist or may have been deleted. Please check the link and try again."
        action={
          <div className="text-center text-sm opacity-75">
            If you believe this is an error, please contact the person who shared this link with
            you.
          </div>
        }
      />
    );
  }

  if (isAuthError) {
    return (
      <NonIdealPage
        icon="lock"
        title="Access Not Authorized"
        description="You don't have permission to view this content. The sharing link may be invalid or expired, or you may need to request access from the content owner."
        action={
          <div className="text-center text-sm opacity-75">
            If you believe this is an error, please contact the person who shared this link with you
            or your Recap Workspace Admin.
          </div>
        }
      />
    );
  }

  // For other errors, fall back to a generic embed error view
  return (
    <NonIdealPage
      icon={<Icon icon="error" intent="danger" size={48} />}
      title="Unable to Load Content"
      description="There was a problem loading this content. This may be temporary. Try refreshing the page."
      action={
        <ClarionButton onClick={() => window.location.reload()} intent="primary" size="large">
          Refresh Page
        </ClarionButton>
      }
    />
  );
}
